<!--
  ng-template alone does NOT give a single shared instance: ngTemplateOutlet creates a
  new component instance at every place it's stamped out. The desktop and mobile docks
  below are therefore wrapped in @if (isMobile()) so only ONE of them is ever mounted,
  which is what actually keeps each tab (Layers/Calculations/Scenario/Results) to a
  single DOM/component instance.
-->
<ng-template #layersContent><app-layers-tab></app-layers-tab></ng-template>
<ng-template #cmsContent><app-cms-tab></app-cms-tab></ng-template>
<ng-template #scenarioContent><app-scenario-tab></app-scenario-tab></ng-template>
<ng-template #resultsContent><app-results-tab></app-results-tab></ng-template>

@if (!isMobile()) {
<!-- Desktop: Layers/Calculations/... docked left -->
<div class="dock dock-left">
  <div class="tab-button" (click)="isProcessing ? null : leftPanel.togglePanel()">
    @if (displayLayerTab) {
      <span class="tab-title">
        <mat-icon>layers</mat-icon>
        Layers
      </span>
    }
    @if (displayCMTab) {
      <span class="tab-title">
        <mat-icon>calculate</mat-icon>
        Calculations
      </span>
    }
    @if (displayScenarioTab) {
      <span class="tab-title">
        <mat-icon>content_paste_search</mat-icon>
        Scenario
      </span>
    }

    <button
      matIconButton
      aria-label="Expand panel"
      [disabled]="isProcessing"
      disabledInteractive
      matTooltip="Cannot close panel while processing"
      [matTooltipDisabled]="!isProcessing"
    >
      @if (!leftPanel.isOpen$()) {
        <mat-icon>expand_more</mat-icon>
      } @else {
        <mat-icon>expand_less</mat-icon>
      }
    </button>
  </div>

  @if (leftPanel.isOpen$()) {
    <div class="container-panel">
      <!--
        A Tooltip cannot be displayed here because the mat-tab is disabled.
        Angular Material prevents events on the tab label when the tab is disabled,
        so matTooltip cannot trigger.
        -->
      <mat-tab-group
        class="mat-tab-scroll"
        [selectedIndex]="leftSelectedTabIndex()"
        (selectedIndexChange)="onLeftTabIndexChange($event)"
      >
        @if (displayLayerTab) {
          <mat-tab [disabled]="isProcessing">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>layers</mat-icon>
                Layers
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="layersContent"></ng-container>
          </mat-tab>
        }
        @if (displayCMTab) {
          <mat-tab [disabled]="scenarioService.isDataLoading$()">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>calculate</mat-icon>
                Calculations
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="cmsContent"></ng-container>
          </mat-tab>
        }
        @if (displayScenarioTab) {
          <mat-tab [disabled]="calculationModuleService.isCMRunning$()">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>content_paste_search</mat-icon>
                Scenario
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="scenarioContent"></ng-container>
          </mat-tab>
        }
      </mat-tab-group>
    </div>
  }
</div>

<!-- Desktop: Results docked right -->
<div class="dock dock-right">
  <div class="tab-button" (click)="rightPanel.togglePanel()">
    <button matIconButton aria-label="Expand panel">
      @if (!rightPanel.isOpen$()) {
        <mat-icon>expand_more</mat-icon>
      } @else {
        <mat-icon>expand_less</mat-icon>
      }
    </button>
    <span class="tab-title"><mat-icon>assessment</mat-icon>Results</span>
  </div>

  @if (rightPanel.isOpen$()) {
    <div class="container-panel">
      <ng-container [ngTemplateOutlet]="resultsContent"></ng-container>
    </div>
  }
</div>
}

@if (isMobile()) {
<!-- Mobile: all tabs combined -->
<div class="dock dock-mobile">
  <div class="tab-button" (click)="isProcessing ? null : mobilePanel.togglePanel()">
    <span class="tab-title">Menu</span>
    <button
      class="toggle-button"
      matIconButton
      aria-label="Expand panel"
      [disabled]="isProcessing"
      disabledInteractive
    >
      @if (!mobilePanel.isOpen$()) {
        <mat-icon>expand_more</mat-icon>
      } @else {
        <mat-icon>expand_less</mat-icon>
      }
    </button>
  </div>

  @if (mobilePanel.isOpen$()) {
    <div class="container-panel">
      <mat-tab-group
        #mobileTabGroup
        class="mat-tab-scroll"
        [selectedIndex]="mobileSelectedTabIndex()"
        (selectedIndexChange)="onMobileTabIndexChange($event)"
      >
        @if (displayLayerTab) {
          <mat-tab #layerTab [disabled]="isProcessing">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>layers</mat-icon>
                @if (
                  mobileTabGroup._tabs.toArray()[mobileTabGroup.selectedIndex ?? -1] === layerTab
                ) {
                  Layers
                }
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="layersContent"></ng-container>
          </mat-tab>
        }
        @if (displayCMTab) {
          <mat-tab #cmTab [disabled]="scenarioService.isDataLoading$()">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>calculate</mat-icon>
                @if (mobileTabGroup._tabs.toArray()[mobileTabGroup.selectedIndex ?? -1] === cmTab) {
                  Calculations
                }
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="cmsContent"></ng-container>
          </mat-tab>
        }
        @if (displayScenarioTab) {
          <mat-tab #scenarioTab [disabled]="calculationModuleService.isCMRunning$()">
            <ng-template mat-tab-label>
              <span class="tab-title">
                <mat-icon>content_paste_search</mat-icon>
                @if (
                  mobileTabGroup._tabs.toArray()[mobileTabGroup.selectedIndex ?? -1] === scenarioTab
                ) {
                  Scenario
                }
              </span>
            </ng-template>
            <ng-container [ngTemplateOutlet]="scenarioContent"></ng-container>
          </mat-tab>
        }
        <mat-tab #resultTab>
          <ng-template mat-tab-label>
            <span class="tab-title">
              <mat-icon>assessment</mat-icon>
              @if (
                mobileTabGroup._tabs.toArray()[mobileTabGroup.selectedIndex ?? -1] === resultTab
              ) {
                Results
              }
            </span>
          </ng-template>
          <ng-container [ngTemplateOutlet]="resultsContent"></ng-container>
        </mat-tab>
      </mat-tab-group>
    </div>
  }
</div>
}
